import type {
  FiCatalogResponse,
  FiDataset,
  FiLimits,
  FiWordCloudDataset,
} from "@mapmycustomers/shared/types/entity/fieldInsight";

import {
  isHeaderDateParam,
  requiredParamsMissing,
} from "../RevenueCharts/FieldInsightCharts/initDefaultParams";
import type { DatasetConfig } from "../types";

import { DATASET_MIN_EMAILS, DATASET_MIN_NOTES_PER_ENTITY, DEFAULT_DATASET_CONFIG } from "./consts";
import getDataPointBudget, { getDatasetDataPointCounts } from "./getDataPointBudget";

export const getDatasetTotalNotes = (dataset: DatasetConfig): number =>
  dataset.entities.reduce((total, entity) => total + (entity.noteCount ?? 0), 0);

export const isNoteCountValid = (noteCount: number | undefined, limits: FiLimits): boolean =>
  noteCount !== undefined &&
  Number.isInteger(noteCount) &&
  noteCount >= DATASET_MIN_NOTES_PER_ENTITY &&
  noteCount <= limits.notes.maxPerEntity;

export const isEmailCountValid = (emailCount: number | undefined, limits: FiLimits): boolean =>
  emailCount !== undefined &&
  Number.isInteger(emailCount) &&
  emailCount >= DATASET_MIN_EMAILS &&
  emailCount <= limits.emails.maxTotal;

export const isDatasetValid = (dataset: DatasetConfig, limits: FiLimits): boolean =>
  dataset.entities.length > 0 &&
  dataset.entities.every((entity) => isNoteCountValid(entity.noteCount, limits)) &&
  getDatasetTotalNotes(dataset) <= limits.notes.maxTotal &&
  (!dataset.emails || isEmailCountValid(dataset.emailCount, limits));

// Asking for both kinds covers the shared total and either per-kind cap, whichever is set.
// Header-bound date params are exempt: the BE injects the selected period at generation time.
export const isDataPointSelectionValid = (
  dataset: DatasetConfig,
  charts: FiCatalogResponse[] | undefined,
  limits: FiLimits
): boolean => {
  const counts = getDatasetDataPointCounts(dataset);
  return (
    getDataPointBudget(counts, limits.dataPoints, "charts").remaining >= 0 &&
    getDataPointBudget(counts, limits.dataPoints, "customFields").remaining >= 0 &&
    (dataset.chartData ?? []).every((chart) => {
      const catalogChart = charts?.find((candidate) => candidate.id === chart.id);
      return (
        !catalogChart ||
        !requiredParamsMissing(
          catalogChart.params.filter((param) => !isHeaderDateParam(param)),
          chart.params
        )
      );
    })
  );
};

const wordCloudDatasetToApi = (dataset: DatasetConfig): FiWordCloudDataset => ({
  entities: dataset.entities.flatMap((entity) =>
    entity.noteCount === undefined ? [] : [{ ...entity, noteCount: entity.noteCount }]
  ),
});

export const datasetToApi = (dataset: DatasetConfig): FiDataset => ({
  ...(dataset.chartData?.length ? { chartData: dataset.chartData } : {}),
  ...(dataset.customFields?.length ? { customFields: dataset.customFields } : {}),
  ...(dataset.emails ? { emailCount: dataset.emailCount, emails: true } : {}),
  ...wordCloudDatasetToApi(dataset),
});

export const datasetFromApi = (dataset: FiDataset | null | undefined): DatasetConfig =>
  dataset && dataset.entities.length > 0
    ? {
        chartData: dataset.chartData,
        customFields: dataset.customFields,
        emailCount: dataset.emailCount,
        emails: dataset.emails,
        entities: dataset.entities,
      }
    : DEFAULT_DATASET_CONFIG;
